import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import HeadingComponent from "../../../../component/atom/Headings/Heading";
import TableComponent from "../../../../component/atom/Table/TableComponent";
import {
  DialogComponent,
  IconButtonComponent,
  SwitchButtonComponet,
} from "../../../../component/atom";
import TextButtonComponet from "../../../../component/atom/Buttons/TextButton";
import {
  AdminDocumentAdd,
  PopUpMessageComponent,
  AdminDocumentFolderAdd,
  DocumentAssociationDialog,
} from "../../../../component/molecule";
import DocumentBreadcrumbs from "../../../../component/molecule/Document/BreadCrumb";
import { Actions } from "../../../../core/modules/Actions";
import { connect } from "react-redux";
import _ from "lodash";
import DropDownComponent from "../../../../component/atom/Inputs/DropDown";
import {
  ASSOCIATION_ACTION,
  checkIsImageFile,
  getText,
  renderTruncatedText,
} from "../../../../core/Constant";
import CircularProgress from "@mui/material/CircularProgress";
import CollectionsIcon from '@mui/icons-material/Collections';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useNavigate } from "react-router-dom";
import AdminDocumentEdit from "../../../../component/molecule/Document/AdminDocumentEdit";
import AdminFolderEditModal from "../../../../component/molecule/Dialog/AdminFolderEditModal";

const IS_DIRECTORY_FOLDER = 1;
const ROOT_BREADCRUMB = { id: null, name: "Documents" };

const isFolder = (doc) => Number(doc?.is_directory) === IS_DIRECTORY_FOLDER;

const normalizeParentId = (parentId) =>
  parentId == null || parentId === 0 ? null : Number(parentId);

const FOLDER_CONFIRM_DELETE_MSG =
  "This action will delete all files which are inside this folder.";
const FOLDER_CONFIRM_STATUS_OFF_MSG =
  "This action will disable access to all files which are inside this folder.";
const FOLDER_CONFIRM_STATUS_ON_MSG =
  "This action will enable the access of all the files which are inside this folder.";

const getFolderConfirmTitle = (confirm) =>
  confirm?.action === "delete" ? "DELETE FOLDER" : "CHANGE FOLDER STATUS";

const getFolderConfirmMessage = (confirm) => {
  if (!confirm) return "";
  if (confirm.action === "delete") return FOLDER_CONFIRM_DELETE_MSG;
  return confirm.nextStatus === 1
    ? FOLDER_CONFIRM_STATUS_ON_MSG
    : FOLDER_CONFIRM_STATUS_OFF_MSG;
};

const AdminDocumentScreen = ({
  documentList,
  deleteDocument,
  courseList,
  getCourseList,
  getDocumentList,
  updateDocumentStatus,
  updateDocument,
  checkDocumentAssociation,
}) => {
  const [create, setCreate] = useState(false);
  const [createFolder, setCreateFolder] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const addMenuOpen = Boolean(addMenuAnchor);
  const navigate = useNavigate();
  const [pendingFileDeleteId, setPendingFileDeleteId] = useState(null);
  const [newcourseList, setCourseList] = useState([]);
  const [course, setCourse] = useState("");
  const [documents, setDocuments] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [folderConfirm, setFolderConfirm] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [documentAssociation, setDocumentAssociation] = useState(null);
  useEffect(() => {
    if (documentList?.documents) {
      setDocuments(documentList.documents);
    }

    if (documentList?.base_url) {
    setBaseUrl(documentList.base_url);
  }
  }, [documentList]);

  useEffect(() => {
    getCourseList();
  }, []);

  useEffect(() => {
    const list = _.map(courseList, (item) => {
      return {
        id: item.id,
        course_id: item.id,
        name: item.name,
      };
    });
    setCourseList(list);
    setCourse(courseList[0]?.id);
    localStorage.setItem("courseId", courseList[0]?.id);
    getDocumentList({ course_id: courseList[0]?.id });
  }, [courseList]);

  const courseName = useMemo(() => {
    const found = newcourseList.find((c) => String(c.id) === String(course));
    return found?.name || "";
  }, [newcourseList, course]);

  const displayList = useMemo(() => {
    let items = documents;
    const hasParentIds = items.some(
      (d) => d.parent_id != null && d.parent_id !== "" && d.parent_id !== 0
    );
    if (hasParentIds) {
      const parentId = currentFolderId ?? null;
      items = items.filter(
        (d) => normalizeParentId(d.parent_id) === normalizeParentId(parentId)
      );
    }
    return items;
  }, [documents, currentFolderId]);

  const getDocumentById = (id) =>
    documents.find((d) => String(d.id) === String(id));

  const handleViewDocument = async (doc) => {
    if (!doc?.id) return;
    try {
      navigate("/admin-view-document", { state: { document: doc, returnPath: "/admin-document" } });
    } catch (error) {
      console.error("Error navigating to document view:", error);
    }
  };

  const resetFolderNavigation = () => {
    setCurrentFolderId(null);
    setFolderStack([]);
  };

  const fetchDocuments = (parentId = null, search = searchQuery) => {
    if (!course) return;
    if (parentId != null && parentId !== "" && parentId !== 0) {
      localStorage.setItem("documentParentId", String(parentId));
    } else {
      localStorage.removeItem("documentParentId");
    }
    const params = parentId != null && parentId !== 0
      ? { course_id: course, parent_id: parentId }
      : { course_id: course };
    if (search?.trim()) {
      params.search = search.trim();
      localStorage.setItem("documentSearch", search.trim());
    } else {
      localStorage.removeItem("documentSearch");
    }

    getDocumentList(params);
  };

  const searchInput = (value) => {
    setSearchQuery(value);
    fetchDocuments(currentFolderId, value);
  };

  const openFolder = async (doc) => {
    if (folderLoading) return;

    setFolderLoading(true);

    try {
      setCurrentFolderId(doc.id);

      setFolderStack((prev) => {
        const lastFolder = prev[prev.length - 1];

        if (lastFolder?.id === doc.id) {
          return prev;
        }

        return [...prev, { id: doc.id, name: doc.name }];
      });

      fetchDocuments(doc.id);
    } finally {
      setTimeout(() => {
        setFolderLoading(false);
      }, 500);
    }
  };

  const navigateToBreadcrumb = (index) => {
    if (index === 0) {
      resetFolderNavigation();
      fetchDocuments(null);
      return;
    }
    if (courseName && index === 1) {
      resetFolderNavigation();
      fetchDocuments(null);
      return;
    }
    const folderIndex = index - (courseName ? 2 : 1);
    const newStack = folderStack.slice(0, folderIndex + 1);
    setFolderStack(newStack);
    const folderId = newStack[folderIndex]?.id ?? null;
    setCurrentFolderId(folderId);
    fetchDocuments(folderId);
  };

  const breadcrumbTrail = useMemo(() => {
    const trail = [ROOT_BREADCRUMB];
    if (courseName) {
      trail.push({ id: `course-${course}`, name: courseName });
    }
    return [...trail, ...folderStack];
  }, [courseName, course, folderStack]);

  const createParentId = useMemo(() => {
    const lastCrumb = breadcrumbTrail[breadcrumbTrail.length - 1];
    if (!lastCrumb?.id) return null;
    const crumbId = String(lastCrumb.id);
    if (crumbId.startsWith("course-")) {
      return course || null;
    }
    return lastCrumb.id;
  }, [breadcrumbTrail, course]);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: true,
        sort: false,
        display: false,
      },
    },
    {
      name: "name",
      label: "NAME",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const doc = getDocumentById(tableMeta.rowData[0]);
          if (!doc) return value;
          const folder = isFolder(doc);

          const isImageFile = checkIsImageFile(doc.name, folder);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: folderLoading ? "wait" : folder ? "pointer" : "default",
                pointerEvents: folderLoading ? "none" : "auto",
              }}
              onClick={() => folder && openFolder(doc)}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: folder ? "#9834F0" : isImageFile ? "#E8EEF3" : "#E8EEF3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {folder ? (
                  <FolderIcon sx={{ color: "#fff", fontSize: 20 }} />
                ) : isImageFile ? (
                  <CollectionsIcon sx={{ color: "#9834F0", fontSize: 20 }} />
                ) : (
                  <PictureAsPdfIcon
                    sx={{ color: "#9834F0", fontSize: 20 }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  color: folder ? "#9834F0" : "#1a1a1a",
                  "&:hover": folder ? { textDecoration: "underline" } : {},
                }}
              >
                {renderTruncatedText(doc.name, 15)}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "is_directory",
      label: "TYPE",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) =>
          Number(value) === IS_DIRECTORY_FOLDER ? "Folder" : "File",
      },
    },
    {
      name: "status",
      label: "STATUS",
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <SwitchButtonComponet
              checked={value == 1}
              onChange={() => statusUpdate(tableMeta, value)}
              inputProps={{ "aria-label": "controlled" }}
            />
          );
        },
      },
    },

    {
      name: "path",
      label: "ACTION",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const doc = getDocumentById(tableMeta.rowData[0]);
          if (!doc) return null;
          return (
            <Typography
              component="span"
              onClick={() => isFolder(doc) ? openFolder(doc) : handleViewDocument(doc)}
              sx={{
                color: "#9834F0",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {isFolder(doc) ? "Open" : "View"}
            </Typography>
          );
        },
      },
    },
     {
          name: "course_id",
          label: "EDIT",
          options: {
            filter: true,
            sort: false,
            customBodyRender: (value, tableMeta, updateValue) => {
              return (
                <IconButtonComponent
                  btnType={"editbtn"}
                  onclick={() => {
                    const doc = getDocumentById(tableMeta.rowData[0]);

                    editDocument(doc);
                  }}
                />
          );
        },
      },
    },
    {
      name: "course_id",
      label: "DELETE",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <IconButtonComponent
              btnType={"deleteIconbtn"}
              onclick={() => deleteBtnpress(tableMeta)}
            />
          );
        },
      },
    },
  ];

  const showDeleteConfirmation = (doc) => {
    localStorage.setItem(
      "deleteDocumentType",
      isFolder(doc) ? "folder" : "file"
    );

    if (isFolder(doc)) {
      setFolderConfirm({ action: ASSOCIATION_ACTION.DELETE, docId: doc.id, understood: false });
      return;
    }
    setPendingFileDeleteId(doc.id);
  };

  const checkAssociationBeforeAction = async (doc, action) => {
    if (!checkDocumentAssociation || !doc?.id) return false;

    const response = await checkDocumentAssociation(doc.id);
    if (response?.error) return false;

    const associationData = response && Object.prototype.hasOwnProperty.call(response, "data")
      ? response.data?.data ?? response.data
      : response;
    if (associationData?.is_associated !== true) return false;

    const hasAssociationData = Array.isArray(associationData)
      ? associationData.length > 0
      : associationData && typeof associationData === "object"
        ? Object.keys(associationData).length > 0
        : Boolean(associationData);

    if (!hasAssociationData) return false;

    setDocumentAssociation({
      data: associationData,
      documentName: doc.name,
      document: doc,
      action,
    });
    return true;
  };

  const editDocument = async (doc) => {
    if (await checkAssociationBeforeAction(doc, ASSOCIATION_ACTION.EDIT)) return;
    setSelectedDocument(doc);
    setEditDialog(true);
  };

  const deleteBtnpress = async (tableMeta) => {
    const doc = getDocumentById(tableMeta.rowData[0]);
    if (!doc) return;

    if (await checkAssociationBeforeAction(doc, ASSOCIATION_ACTION.DELETE)) return;
    showDeleteConfirmation(doc);
  };

  const continueDocumentAction = () => {
    const pendingAction = documentAssociation;
    setDocumentAssociation(null);
    if (!pendingAction?.document) return;

    if (pendingAction.action === ASSOCIATION_ACTION.DELETE) {
      showDeleteConfirmation(pendingAction.document);
    } else {
      setSelectedDocument(pendingAction.document);
      setEditDialog(true);
    }
  };

  const deleteDocumentById = () => {
    if (pendingFileDeleteId) deleteDocument(pendingFileDeleteId);
    setPendingFileDeleteId(null);
  };

  const confirmFolderAction = () => {
    if (!folderConfirm?.understood || !folderConfirm?.docId) return;
    const { action, docId, nextStatus } = folderConfirm;
    if (action === ASSOCIATION_ACTION.DELETE) {
      deleteDocument(docId);
    } else if (action === "status") {
      updateDocumentStatus({ document_id: docId, status: nextStatus });
      setDocuments((prev) =>
        prev.map((d) =>
          String(d.id) === String(docId) ? { ...d, status: nextStatus } : d
        )
      );
    }
    setFolderConfirm(null);
  };

  const loadCreateDocument = () => {
    setCreate(true)
  };
  const handleAddMenuOpen = (event) => setAddMenuAnchor(event.currentTarget);
  const handleAddMenuClose = () => setAddMenuAnchor(null);
  const loadCreateFolder = () => setCreateFolder(true);

  const handleUploadFile = () => {
    handleAddMenuClose();
    setTimeout(() => loadCreateDocument(), 0);
  };

  const handleCreateFolderOpen = () => {
    handleAddMenuClose();
    setTimeout(() => loadCreateFolder(), 0);
  };

  const handleCreateFolderClose = () => setCreateFolder(false);

  const statusUpdate = (tableMeta, value) => {
    const docId = tableMeta.rowData[0];
    const doc = getDocumentById(docId);
    const nextStatus = value == 1 ? 0 : 1;

    localStorage.setItem(
      "documentStatusType",
      doc && isFolder(doc) ? "folder" : "file"
    );

    if (doc && isFolder(doc)) {
      setFolderConfirm({
        action: "status",
        docId,
        nextStatus,
        understood: false,
      });
      return;
    }
    updateDocumentStatus({
      document_id: docId,
      status: nextStatus,
    });
    setDocuments((prev) =>
      prev.map((d) =>
        String(d.id) === String(docId) ? { ...d, status: nextStatus } : d
      )
    );
  };

  const onCourseChange = (e) => {
    const Selectedvalue = getText(e);
    setCourse(Selectedvalue);
    localStorage.setItem("courseId", Selectedvalue);
    resetFolderNavigation();
    setSearchQuery("");
    localStorage.removeItem("documentSearch");
    getDocumentList({ course_id: Selectedvalue });
  };

  return (
    <>
      <Box className="main-screen-container">
        <Grid container justifyContent={"space-between"} mt={1}>
          <Grid item>
            <HeadingComponent
              text={"Documents"}
              fontweigth={600}
              size={40}
              fontfamily={"Montserrat"}
            />
          </Grid>
          <Grid item justifyContent={"flex-end"} alignItems={"center"} display={"flex"} flexDirection={{
            xs: "column",
            md: "column",
            lg: "row",
            xl: "row",
          }}
            sx={{ gap: 1 }}
          >
            <Box sx={{ width: { xs: "100%", lg: "350px" } }}> 
              <DropDownComponent
                isShowPlaceholder={true}
                isShowZero={newcourseList.length > 0 ? false : true}
                initialValue="Select Course"
                onchange={onCourseChange}
                radius={"15px"}
                list={newcourseList}
                selectedValue={course}
                enableTooltip={true}
                fixedWidth="100%"
              />
            </Box>
            <Box
              sx={{
                width: { xs: "100%",lg: "250px" },
                flexShrink: 0,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={handleAddMenuOpen}
                className="btn btn-primary"
                sx={{
                  width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
                  backgroundColor: "#9834F0 !important",
                  borderColor: "#9834F0 !important",
                  color: "#fff !important",
                  textTransform: "none",
                  borderRadius: "15px", px: 2.5, py: 1, boxShadow: "0 4px 12px rgba(152, 52, 240, 0.35)",
                  "&:hover": { backgroundColor: "#7a29c0 !important", borderColor: "#7a29c0 !important" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add New</span>
                </Box>
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={addMenuOpen}
                onClose={handleAddMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: "200px",
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                  },
                }}
                MenuListProps={{ sx: { width: "100%", py: 0.5 } }}
              >
                <MenuItem
                  onClick={handleCreateFolderOpen}
                  sx={{
                    py: 1.5,
                    gap: 1,
                    "&:hover": {
                      backgroundColor: "rgba(122, 41, 192, 0.12) !important",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "#9834F0" }}>
                    <CreateNewFolderOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Create Folder
                </MenuItem>
                <MenuItem
                  onClick={handleUploadFile}
                  sx={{
                    py: 1.5,
                    gap: 1,
                    "&:hover": {
                      backgroundColor: "rgba(122, 41, 192, 0.12) !important",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "#9834F0" }}>
                    <UploadFileOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Upload File
                </MenuItem>
              </Menu>
            </Box>
          </Grid>
        </Grid>

        {/* BREADCRUMBS COMPONENT */}
        <DocumentBreadcrumbs
          breadcrumbTrail={breadcrumbTrail}
          onBreadcrumbClick={navigateToBreadcrumb}
        />

        <Box
          className="common-admin-content-wrap"
          sx={{
            bgcolor: "#fff",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}
        >

          <TableComponent
            columns={columns}
            data={displayList}
            filter={false}
            onSearchChange={searchInput}
          />
        </Box>
      </Box>

      {/* PopUp message configuration */}
      <PopUpMessageComponent
        open={pendingFileDeleteId != null}
        type={"other"}
        title={"Delete!"}
        message={"Are you sure you want to delete document?"}
        btntext={"Yes, delete"}
        altbtntext={"No"}
        altonclick={() => setPendingFileDeleteId(null)}
        onclick={deleteDocumentById}
        onclose={() => setPendingFileDeleteId(null)}
      />

      {/* Confirmation Dialog */}
      <DialogComponent
        isShowCloseButton={true}
        title={getFolderConfirmTitle(folderConfirm)}
        open={Boolean(folderConfirm)}
        maxwidth="sm"
        onClose={() => setFolderConfirm(null)}
      >
        <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ color: "#2D3945", fontSize: 16, lineHeight: 1.5, marginTop: 2, justifyContent: "center" }}>
            {getFolderConfirmMessage(folderConfirm)}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(folderConfirm?.understood)}
                  onChange={(e) =>
                    setFolderConfirm((prev) =>
                      prev ? { ...prev, understood: e.target.checked } : prev
                    )
                  }
                  sx={{
                    color: "#9834F0",
                    "&.Mui-checked": { color: "#9834F0" },
                  }}
                />
              }
              label="I understand"
              sx={{
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#2D3945",
                },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            <TextButtonComponet
              text="Cancel"
              classStyle="btn btn-secondary"
              onButtonClick={() => setFolderConfirm(null)}
            />
            <TextButtonComponet
              text="OK"
              isDisabled={!folderConfirm?.understood}
              onButtonClick={confirmFolderAction}
            />
          </Box>
        </Box>
      </DialogComponent>

      {/* Upload file popup */}
      <DialogComponent
        isShowCloseButton={true}
        title={"Upload File"}
        open={create}
        onClose={() => setCreate(false)}
      >
        <AdminDocumentAdd
          key={create ? "upload-file-open" : "upload-file-closed"}
          courseId={course}
          current_document_id={documentList?.current_document_id ?? documentList?.current_document?.id}
          onClose={() => setCreate(false)}
          baseUrl={baseUrl}
        />
      </DialogComponent>

      {/* Create folder popup */}
      <DialogComponent
        isShowCloseButton={true}
        title={"CREATE FOLDER"}
        open={createFolder}
        maxwidth="sm"
        onClose={handleCreateFolderClose}
      >
        <AdminDocumentFolderAdd
          key={createFolder ? "create-folder-open" : "create-folder-closed"}
          courseId={course}
          parentId={createParentId}
          current_document_id={documentList?.current_document_id ?? documentList?.current_document?.id}
          existingItems={displayList}
          onClose={handleCreateFolderClose}
        />
      </DialogComponent>
      {folderLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(255,255,255,0.4)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Edit Dialog Component */}
      <DialogComponent
        isShowCloseButton={true}
        title={`EDIT ${selectedDocument?.is_directory == 1 ? "FOLDER" : "FILE"}`}
        open={editDialog}
        maxwidth="sm"
        onClose={() => {
          setEditDialog(false);
          setSelectedDocument(null);
        }}
      >
        {selectedDocument?.is_directory == 1 ? (
          <AdminFolderEditModal
            folder={selectedDocument}
            baseUrl={baseUrl}
            updateDocument={updateDocument}
            onClose={() => {
              setEditDialog(false);
              setSelectedDocument(null);
            }}
            onSuccess={() => {
              setEditDialog(false);
              setSelectedDocument(null);
              fetchDocuments(currentFolderId);
            }}
          />
        ) : (
          <AdminDocumentEdit
            documentData={selectedDocument}
            baseUrl={baseUrl}
            updateDocument={updateDocument}
            onClose={() => {
              setEditDialog(false);
              setSelectedDocument(null);
            }}
          />
        )}
      </DialogComponent>

      <DocumentAssociationDialog
        association={documentAssociation}
        onClose={() => setDocumentAssociation(null)}
        onConfirm={continueDocumentAction}
      />
    </>
  );
};

export default connect(
  (state) => ({
    documentList: state.document.get("documentList"),
    courseList: state.course.get("commonCourseList"),
  }),
  {
    getDocumentList: Actions.document.getDocumentList,
    deleteDocument: Actions.document.deleteDocument,
    updateDocumentStatus: Actions.document.updateDocumentStatus,
    getCourseList: Actions.course.getCourseList,
    updateDocument: Actions.document.updateDocument,
    checkDocumentAssociation: Actions.document.checkDocumentAssociation,
  }
)(AdminDocumentScreen);
